import React, {useState, useEffect, useContext} from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import {withMutation, ApolloContext} from '../../../../shared/enhancers/graphql-enhancer';
import {SectionHeading} from '../../../../shared/library/cards/card';
import AlternateDecisions from '../../../../shared/library/cards/alternate-decision';
import AlternateAdvice from '../../../../shared/library/cards/stack-advice';
import LoadMoreButton from '../../../../shared/library/buttons/load-more';
import {formatToolList} from '../../../../shared/utils/format-tool-list';
import {flattenEdges} from '../../../../shared/utils/graphql';
import {callTrackViews} from '../../../../shared/utils/trackViews.js';
import {trackViews} from '../../../../data/shared/mutations.js';
import {
  stackDecisions,
  privateStackDecisions,
  stackAdvice,
  privateStackAdvice
} from '../../../../data/stackups/queries';

const Container = glamorous.div({
  ' > div:last-of-type': {
    borderBottom: 0
  }
});

const ShowMoreContainer = glamorous.div({
  marginTop: 30,
  display: 'flex',
  justifyContent: 'center',
  zIndex: 1
});

const HiddenDecisions = glamorous.div({
  display: 'none'
});

const SHOW_DECISION_COUNT = 10;

const StackDecisions = ({items, tools, slug, companyName, isAdvice, isPrivate, trackViews}) => {
  const client = useContext(ApolloContext);
  const initialDecs = flattenEdges(items);
  const [decisionsToShow, setDecisionsToShow] = useState(initialDecs.slice(0, SHOW_DECISION_COUNT));
  const [endCursor, setEndCursor] = useState(items.pageInfo.endCursor);
  const [isLoading, setIsLoading] = useState(false);

  const contextName = isAdvice
    ? isPrivate
      ? 'PrivateStackAdvice'
      : 'StackAdvice'
    : isPrivate
    ? 'PrivateStackDecisions'
    : 'StackDecisions';
  const queryName = isAdvice
    ? isPrivate
      ? 'privateStackDecisionsGetAdvice'
      : 'stackDecisionsGetAdvice'
    : isPrivate
    ? 'privateStackDecisions'
    : 'stackDecisions';

  const query = isAdvice
    ? isPrivate
      ? privateStackAdvice
      : stackAdvice
    : isPrivate
    ? privateStackDecisions
    : stackDecisions;

  useEffect(() => {
    if (trackViews && initialDecs) {
      fireTrackViews(initialDecs.slice(0, decisionsToShow.length));
    }
  }, []);

  const title = isAdvice
    ? `${isPrivate ? 'Private ' : ''}Advice on ${formatToolList(tools)}`
    : `Decisions ${isPrivate ? `at ${companyName} ` : ''}about ${formatToolList(tools)}`;

  const showMoreDecisions = () => {
    let newCount = decisionsToShow.length + SHOW_DECISION_COUNT;
    if (newCount > items.count) newCount = items.count;
    if (newCount > initialDecs.length) {
      setIsLoading(true);
      client
        .query({
          query: query,
          variables: {slug: slug, after: endCursor}
        })
        .then(({data}) => {
          setDecisionsToShow(prevDecisions => [
            ...prevDecisions,
            ...flattenEdges(data.stackupBySlug[queryName])
          ]);
          setEndCursor(data.stackupBySlug[queryName].pageInfo.endCursor);
          fireTrackViews(flattenEdges(data.stackupBySlug[queryName]));
          setIsLoading(false);
        });
    } else {
      setDecisionsToShow([...initialDecs.slice(0, newCount)]);
      fireTrackViews(initialDecs.slice(decisionsToShow.length, newCount));
    }
  };

  const fireTrackViews = decisions => {
    const decisionIds = [];
    if (isAdvice)
      decisions.map(i => decisionIds.push(i.id, ...i.answers.edges.map(ans => ans.node.id)));
    else decisions.map(i => decisionIds.push(i.id));

    callTrackViews({
      trackViews,
      decisionIds,
      clientContext: `${contextName}-${window.location.pathname}`
    });
  };

  return (
    <Container>
      <SectionHeading>{title}</SectionHeading>
      {decisionsToShow.length > 0 &&
        (isAdvice ? (
          <AlternateAdvice advice={decisionsToShow} formatData={des => des.node} />
        ) : (
          <AlternateDecisions decisions={decisionsToShow} formatData={des => des.node} />
        ))}
      {decisionsToShow.length <= 15 && !isPrivate && (
        <HiddenDecisions>
          {isAdvice ? (
            <AlternateAdvice
              advice={(initialDecs || []).slice(decisionsToShow.length)}
              formatData={des => des.node}
            />
          ) : (
            <AlternateDecisions
              decisions={(initialDecs || []).slice(decisionsToShow.length)}
              formatData={des => des.node}
            />
          )}
        </HiddenDecisions>
      )}
      <ShowMoreContainer>
        {decisionsToShow.length < items.count && (
          <LoadMoreButton loading={isLoading} onClick={showMoreDecisions} />
        )}
      </ShowMoreContainer>
    </Container>
  );
};

StackDecisions.propTypes = {
  slug: PropTypes.string,
  items: PropTypes.array,
  tools: PropTypes.array,
  companyName: PropTypes.string,
  isAdvice: PropTypes.bool,
  isPrivate: PropTypes.bool,
  trackViews: PropTypes.func
};

export default withMutation(trackViews, mutate => ({
  trackViews: (decisionIds, clientContext) => mutate({variables: {decisionIds, clientContext}})
}))(StackDecisions);
